import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {colors, fonts, radii, spacing, typography} from '../config/theme';
import {
  formatFuelPrice,
  isStalePrice,
  shortFuelName,
} from '../utility/fuelOfferings';

// How many fuel chips fit on a card before the rest collapse into "+N more".
const CARD_CHIP_LIMIT = 4;

const CATEGORY_KEYS = {
  petrol: 'root.PumpSearch.FuelCategory.Petrol',
  diesel: 'root.PumpSearch.FuelCategory.Diesel',
  lpg: 'root.PumpSearch.FuelCategory.Lpg',
  cng: 'root.PumpSearch.FuelCategory.Cng',
};

const categoryLabel = (category, t) =>
  CATEGORY_KEYS[category] ? t(CATEGORY_KEYS[category]) : category;

/**
 * Compact row of fuel prices for a station card. The user's fuel comes
 * first and is highlighted; the rest follow in category order.
 * Renders nothing when the station has no fuel data.
 */
export const FuelPriceChips = ({offerings}) => {
  const {t} = useTranslation();
  const {prices, unpricedCategories} = offerings;

  if (prices.length === 0) {
    if (unpricedCategories.length === 0) {
      return null;
    }
    // Known fuels without prices: say what is sold here.
    return (
      <View style={styles.chipRow}>
        {unpricedCategories.map(category => (
          <View key={category} style={[styles.chip, styles.chipUnpriced]}>
            <Text style={styles.chipName}>{categoryLabel(category, t)}</Text>
          </View>
        ))}
      </View>
    );
  }

  const ordered = [
    ...prices.filter(fuel => fuel.isSelected),
    ...prices.filter(fuel => !fuel.isSelected),
  ];
  const shown = ordered.slice(0, CARD_CHIP_LIMIT);
  const hidden = ordered.length - shown.length;

  return (
    <View style={styles.chipRow}>
      {shown.map(fuel => (
        <View
          key={fuel.fuelOptionId ?? fuel.code}
          style={[styles.chip, fuel.isSelected && styles.chipSelected]}>
          <Text
            style={[styles.chipName, fuel.isSelected && styles.chipNameSelected]}
            numberOfLines={1}>
            {shortFuelName(fuel)}
          </Text>
          <Text
            style={[
              styles.chipPrice,
              isStalePrice(fuel.updatedAt) && styles.textStale,
            ]}
            numberOfLines={1}>
            €{formatFuelPrice(fuel.price)}
          </Text>
        </View>
      ))}
      {hidden > 0 && (
        <View style={[styles.chip, styles.chipMore]}>
          <Text style={styles.chipMoreText}>
            {t('root.PumpSearch.MoreFuels', {count: hidden})}
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Full fuel price list for the details sheet, grouped by category.
 * `formatAge(ms)` turns a timestamp into "Updated 3 hours ago". When every
 * price shares the same age it is shown once under the title.
 */
export const FuelPriceList = ({offerings, formatAge}) => {
  const {t} = useTranslation();
  const {prices, groups, unpricedCategories, hasEv, evConnectors} = offerings;

  const ageOf = fuel => {
    const ms = fuel.updatedAt ? Date.parse(fuel.updatedAt) : NaN;
    return Number.isFinite(ms) ? formatAge(ms) : '';
  };
  const ages = new Set(prices.map(ageOf));
  const sharedAge = ages.size === 1 ? [...ages][0] : null;
  const hasExtras = unpricedCategories.length > 0 || hasEv;

  return (
    <View style={styles.list}>
      <View style={styles.listHeader}>
        <Text style={styles.listTitle}>{t('root.PumpSearch.FuelPrices')}</Text>
        {!!sharedAge && (
          <View style={styles.listAgeRow}>
            <View
              style={[
                styles.liveDot,
                isStalePrice(prices[0].updatedAt) && styles.liveDotStale,
              ]}
            />
            <Text style={styles.listAge}>{sharedAge}</Text>
          </View>
        )}
      </View>

      {prices.length === 0 && (
        <Text style={styles.emptyText}>{t('root.PumpSearch.NoFuelPrices')}</Text>
      )}

      {groups.map(group => (
        <View key={group.category} style={styles.group}>
          <Text style={styles.groupLabel}>
            {categoryLabel(group.category, t)}
          </Text>
          {group.fuels.map((fuel, index) => {
            const stale = isStalePrice(fuel.updatedAt);
            return (
              <View
                key={fuel.fuelOptionId ?? fuel.code}
                style={[
                  styles.row,
                  index > 0 && styles.rowDivided,
                  fuel.isSelected && styles.rowSelected,
                ]}>
                <View style={styles.rowInfo}>
                  <View style={styles.rowNameLine}>
                    <Text style={styles.rowName} numberOfLines={1}>
                      {fuel.name || shortFuelName(fuel)}
                    </Text>
                    {fuel.isSelected && (
                      <View style={styles.yourFuelTag}>
                        <Text style={styles.yourFuelTagText}>
                          {t('root.PumpSearch.YourFuel')}
                        </Text>
                      </View>
                    )}
                  </View>
                  {!sharedAge && !!ageOf(fuel) && (
                    <Text
                      style={[styles.rowAge, stale && styles.textStale]}
                      numberOfLines={1}>
                      {ageOf(fuel)}
                    </Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.rowPrice,
                    fuel.isSelected && styles.rowPriceSelected,
                    stale && styles.textStale,
                  ]}>
                  €{formatFuelPrice(fuel.price)}
                  <Text style={styles.rowUnit}> /{fuel.unit || 'L'}</Text>
                </Text>
              </View>
            );
          })}
        </View>
      ))}

      {hasExtras && (
        <View style={styles.extras}>
          <Text style={styles.groupLabel}>
            {prices.length > 0
              ? t('root.PumpSearch.AlsoAvailable')
              : t('root.PumpSearch.SoldHere')}
          </Text>
          <View style={[styles.chipRow, styles.chipRowTight]}>
            {unpricedCategories.map(category => (
              <View key={category} style={[styles.chip, styles.chipUnpriced]}>
                <Text style={styles.chipName}>{categoryLabel(category, t)}</Text>
              </View>
            ))}
            {hasEv && (
              <View style={[styles.chip, styles.chipEv]}>
                <Text style={[styles.chipName, styles.chipNameEv]}>
                  {t('root.PumpSearch.EvCharging')}
                  {evConnectors
                    ? ` · ${t('root.PumpSearch.Connectors', {
                        count: evConnectors,
                      })}`
                    : ''}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Card chips
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  chipRowTight: {
    marginTop: spacing.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 6,
    borderRadius: radii.badge,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  chipSelected: {
    borderColor: colors.secondary,
    backgroundColor: colors.mintWash,
  },
  chipUnpriced: {
    borderStyle: 'dashed',
    backgroundColor: colors.background,
  },
  chipEv: {
    borderColor: colors.mintLine,
    backgroundColor: colors.mintWash,
  },
  chipName: {
    ...typography.labelSm,
    color: colors.textMuted,
  },
  chipNameSelected: {
    color: colors.brandLink,
  },
  chipNameEv: {
    color: colors.brandLink,
  },
  chipPrice: {
    ...typography.labelMd,
    fontFamily: fonts.bold,
    color: colors.text,
  },
  chipMore: {
    borderColor: 'transparent',
    backgroundColor: colors.surfaceMuted,
  },
  chipMoreText: {
    ...typography.labelSm,
    color: colors.primary,
  },
  textStale: {
    color: colors.textSubtle,
  },

  // Details list
  list: {
    marginBottom: spacing.md,
    padding: spacing.margin,
    backgroundColor: colors.surface,
    borderRadius: radii.card,
    borderWidth: 1,
    borderColor: colors.border,
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  listTitle: {
    ...typography.headlineSm,
    color: colors.text,
  },
  listAgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
  },
  listAge: {
    ...typography.bodySm,
    color: colors.textMuted,
    flexShrink: 1,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.secondary,
  },
  liveDotStale: {
    backgroundColor: colors.warning,
  },
  emptyText: {
    ...typography.bodyMd,
    color: colors.textMuted,
    marginTop: spacing.sm,
  },
  group: {
    marginTop: spacing.margin,
  },
  groupLabel: {
    ...typography.labelSm,
    color: colors.textSubtle,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    minHeight: 48,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginHorizontal: -spacing.sm,
    borderRadius: radii.sm,
  },
  rowDivided: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  rowSelected: {
    backgroundColor: colors.mintWash,
    borderTopColor: 'transparent',
  },
  rowInfo: {
    flex: 1,
  },
  rowNameLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowName: {
    ...typography.labelLg,
    color: colors.text,
    flexShrink: 1,
  },
  rowAge: {
    ...typography.bodySm,
    color: colors.textMuted,
    marginTop: 2,
  },
  yourFuelTag: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: radii.badge,
    backgroundColor: colors.secondary,
  },
  yourFuelTagText: {
    ...typography.labelSm,
    color: colors.onPrimary,
  },
  rowPrice: {
    ...typography.headlineSm,
    fontFamily: fonts.bold,
    color: colors.text,
  },
  rowPriceSelected: {
    color: colors.primary,
  },
  rowUnit: {
    ...typography.bodySm,
    color: colors.textMuted,
  },
  extras: {
    marginTop: spacing.margin,
  },
});
